import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import Button from "../ui/Button";
import Card from "../ui/Card";
import StatusBadge from "../ui/StatusBadge";
import NewItemButton from "../ui/NewItemButton";
import AddMemberModal from "./AddMemberModal";
import MoveMemberModal from "./MoveMemberModal";
import EditUserModal from "../EditUserModal";
import { formatOrganisationRole } from "../../utils/roles";

export default function TeamMembersPanel({
  organisationId,
  customerId,
  canWrite = true,
  buildUserLink,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [movingMember, setMovingMember] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/admin/organisations/${organisationId}/members`);
      setMembers(res.data || []);
    } catch (err) {
      console.error("Failed to load team", err);
      setError("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organisationId) fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members
      .filter((m) => {
        if (!showArchived && (m.isArchived || m.status === "inactive")) return false;
        if (!q) return true;
        const name = `${m.userId?.firstname || ""} ${m.userId?.lastname || ""}`.toLowerCase();
        const email = (m.userId?.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .sort((a, b) => {
        const nameA = `${a.userId?.lastname || ""}${a.userId?.firstname || ""}`;
        const nameB = `${b.userId?.lastname || ""}${b.userId?.firstname || ""}`;
        return nameA.localeCompare(nameB);
      });
  }, [members, search, showArchived]);

  const userLink = (userId) => {
    if (buildUserLink) return buildUserLink(userId);
    return `/admin/customers/${customerId}/organisations/${organisationId}/users/${userId}`;
  };

  const openEditModal = async (member) => {
    const userId = member.userId?._id;
    if (!userId) return;

    setLoadingUser(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/users/${userId}`, {
        params: { organisationId },
      });
      setEditingUser(res.data);
      setEditModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load user details");
    } finally {
      setLoadingUser(false);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const openMoveModal = (member) => {
    setMovingMember(member);
    setMoveModalOpen(true);
  };

  const closeMoveModal = () => {
    setMoveModalOpen(false);
    setMovingMember(null);
  };

  const memberStatus = (m) =>
    m.isArchived || m.status === "inactive" ? "archived" : "active";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="search"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-2 text-sm flex-1 min-w-[200px]"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
        {canWrite && (
          <NewItemButton
            onClick={() => setIsAddModalOpen(true)}
            label="Add member"
            shortLabel="Add"
          />
        )}
      </div>

      {error && <div className="text-red-600 mb-4">{error}</div>}

      {loading ? (
        <div className="text-center py-6 text-gray-600">Loading team…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-600 py-4">No team members found.</div>
      ) : (
        <>
          <div className="space-y-3 md:hidden mb-4">
            {filtered.map((m) => (
              <Card key={m._id} className="p-4">
                <div className="font-medium">
                  <Link to={userLink(m.userId?._id)} className="text-blue-600 hover:underline">
                    {m.userId?.firstname} {m.userId?.lastname}
                  </Link>
                </div>
                <div className="text-sm text-gray-600">{m.userId?.email}</div>
                <div className="text-sm text-gray-500 mt-1">
                  Role: {formatOrganisationRole(m.role)}
                </div>
                <div className="mt-2">
                  <StatusBadge status={memberStatus(m)} />
                </div>
                {canWrite && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(m)}
                      disabled={loadingUser}
                    >
                      Edit
                    </Button>
                    {memberStatus(m) === "active" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openMoveModal(m)}
                      >
                        Move
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div className="hidden md:block">
            <table className="table-auto w-full bg-white rounded shadow">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Hourly Cost</th>
                  <th className="text-left p-3">Hourly Rate</th>
                  <th className="text-left p-3">Status</th>
                  {canWrite && <th className="text-left p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m._id} className="border-t">
                    <td className="p-3">
                      <Link to={userLink(m.userId?._id)} className="text-blue-600 hover:underline">
                        {m.userId?.firstname} {m.userId?.lastname}
                      </Link>
                    </td>
                    <td className="p-3">{m.userId?.email}</td>
                    <td className="p-3">{formatOrganisationRole(m.role)}</td>
                    <td className="p-3">{m.hourlyCost ?? "—"}</td>
                    <td className="p-3">{m.hourlyRate ?? "—"}</td>
                    <td className="p-3">
                      <StatusBadge status={memberStatus(m)} />
                    </td>
                    {canWrite && (
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(m)}
                            disabled={loadingUser}
                          >
                            Edit
                          </Button>
                          {memberStatus(m) === "active" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openMoveModal(m)}
                            >
                              Move
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AddMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdded={fetchMembers}
        organisationId={organisationId}
      />

      <MoveMemberModal
        isOpen={moveModalOpen}
        onClose={closeMoveModal}
        onMoved={fetchMembers}
        organisationId={organisationId}
        customerId={customerId}
        member={movingMember}
      />

      <EditUserModal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        user={editingUser}
        organisationId={organisationId}
        showArchiveActions
        onUserUpdated={() => {
          closeEditModal();
          fetchMembers();
        }}
      />
    </div>
  );
}
